'use client';

import * as Sentry from '@sentry/nextjs';
import { getUserFacingErrorCopy } from '@/lib/errors/sanitize-error';
import {
  sanitizeObservabilityString,
  sanitizeObservabilityUrl,
} from './privacy';

export interface ErrorCorrelation {
  eventId?: string | null;
  requestId?: string | null;
  supportCode?: string | null;
  digest?: string | null;
  surface: string;
  routePath: string;
  capturedAt: string;
}

declare global {
  interface Window {
    __ECLIPSE_ERROR_CORRELATION__?: ErrorCorrelation;
  }
}

const recentCaptures = new Map<string, ErrorCorrelation>();
const CAPTURE_TTL_MS = 5_000;

function getCurrentRoutePath(): string {
  if (typeof window === 'undefined') {
    return '/';
  }

  return sanitizeObservabilityUrl(
    `${window.location.pathname}${window.location.search}`
  );
}

function pruneRecentCaptures(now: number): void {
  for (const [key, value] of recentCaptures.entries()) {
    if (now - new Date(value.capturedAt).getTime() > CAPTURE_TTL_MS) {
      recentCaptures.delete(key);
    }
  }
}

function buildCaptureKey(
  error: Error & { digest?: string },
  surface: string,
  requestId?: string | null
): string {
  return sanitizeObservabilityString(
    [
      surface,
      error.name,
      error.message,
      error.digest || '',
      requestId || '',
      getCurrentRoutePath(),
    ].join('|')
  );
}

export function rememberErrorCorrelation(
  correlation: ErrorCorrelation
): ErrorCorrelation {
  const sanitizedCorrelation: ErrorCorrelation = {
    ...correlation,
    eventId: correlation.eventId
      ? sanitizeObservabilityString(correlation.eventId)
      : null,
    requestId: correlation.requestId
      ? sanitizeObservabilityString(correlation.requestId)
      : null,
    supportCode: correlation.supportCode
      ? sanitizeObservabilityString(correlation.supportCode)
      : null,
    digest: correlation.digest
      ? sanitizeObservabilityString(correlation.digest)
      : null,
    surface: sanitizeObservabilityString(correlation.surface),
    routePath: sanitizeObservabilityUrl(correlation.routePath),
  };

  if (typeof window !== 'undefined') {
    window.__ECLIPSE_ERROR_CORRELATION__ = sanitizedCorrelation;
  }

  return sanitizedCorrelation;
}

export function getLatestErrorCorrelation(): ErrorCorrelation | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.__ECLIPSE_ERROR_CORRELATION__ ?? null;
}

export function captureClientError(
  error: Error & { digest?: string },
  options: {
    surface: string;
    componentStack?: string;
    requestId?: string | null;
    supportCode?: string | null;
  }
): ErrorCorrelation {
  const existing = getUserFacingErrorCopy(error);
  const requestId = options.requestId ?? existing.requestId ?? null;
  const supportCode =
    options.supportCode ?? existing.ref ?? error.digest ?? null;
  const now = Date.now();

  pruneRecentCaptures(now);

  const captureKey = buildCaptureKey(error, options.surface, requestId);
  const cached = recentCaptures.get(captureKey);
  if (cached) {
    return rememberErrorCorrelation(cached);
  }

  const routePath = getCurrentRoutePath();
  const capturedAt = new Date(now).toISOString();
  const eventId = Sentry.captureException(error, {
    tags: {
      error_surface: options.surface,
    },
    extra: {
      digest: error.digest ?? null,
      requestId,
      supportCode,
      routePath,
      componentStack: options.componentStack
        ? sanitizeObservabilityString(options.componentStack)
        : undefined,
    },
  });

  const correlation: ErrorCorrelation = {
    eventId: eventId || null,
    requestId,
    supportCode,
    digest: error.digest ?? null,
    surface: options.surface,
    routePath,
    capturedAt,
  };

  recentCaptures.set(captureKey, correlation);
  return rememberErrorCorrelation(correlation);
}

export function openUserFeedbackDialog(
  correlation?: ErrorCorrelation | null
): boolean {
  const activeCorrelation = correlation ?? getLatestErrorCorrelation();
  if (!activeCorrelation?.eventId) {
    return false;
  }

  Sentry.showReportDialog({
    eventId: activeCorrelation.eventId,
  });

  return true;
}
